import { cloneDeep } from 'lodash';

const state = {
  filter: {},
  pages: [],
  page: {}
};

const getters = {
  filter: (state) => {
    return state.filter || {};
  },
  pages: (state) => {
    return state.pages || [];
  },
  page: (state) => {
    return state.page || {};
  }
};

const mutations = {
  SET_FILTER(state, item) {
    state.filter = item;
  },
  SET_PAGES(state, list) {
    state.pages = list;
  },
  SET_PAGE(state, item) {
    state.page = item;

    const index = state.pages.findIndex((x) => x.id === item.id);
    if (index < 0) {
      state.pages.push(cloneDeep(item));
    } else {
      this._vm.$set(state.pages, index, cloneDeep(item));
    }
  },
  DELETE_PAGE(state, id) {
    const index = state.pages.findIndex((x) => x.id === id);
    if (index >= 0) {
      state.pages.splice(index, 1);
    }
  }
};

const actions = {
  // eslint-disable-next-line no-empty-pattern
  convertImage({}, req) {
    return new Promise((resolve, reject) => {
      const formData = new FormData();
      formData.append('file', req.file);
      this._vm.$http
        .post('content/convertImage', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        })
        .then((response) => {
          resolve(response.data.imageUrl);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  setFilter({ commit }, item) {
    commit('SET_FILTER', item);
  },

  saveCatalogQuestion({ commit }, question) {
    return new Promise((resolve, reject) => {
      this._vm.$http
        .post('content/saveCatalogQuestion', question)
        .then((response) => {
          resolve();
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  deleteCatalogQuestion({ commit }, id) {
    return new Promise((resolve, reject) => {
      this._vm.$http
        .delete(`content/deleteCatalogQuestion?id=${id}`)
        .then((response) => {
          resolve();
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  // eslint-disable-next-line no-empty-pattern
  addImages({}, files) {
    return new Promise((resolve, reject) => {
      const formData = new FormData();
      for (var file of files) {
        formData.append('file', file);
      }
      this._vm.$http
        .post('content/addImages', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        })
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  getPages({ commit }) {
    return new Promise((resolve, reject) => {
      this._vm.$http
        .get('content/getPages')
        .then((response) => {
          commit('SET_PAGES', response.data);
          resolve();
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  // One page's AI spend broken out by step and section. Resolves to the caller instead of committing:
  // the breakdown is only ever shown inside an expanded list row, so it belongs to that row's lifetime
  // rather than to the page list's state.
  // eslint-disable-next-line no-empty-pattern
  getPageAiSpend({}, pageId) {
    return new Promise((resolve, reject) => {
      this._vm.$http
        .get(`content/getPageAiSpend?pageId=${pageId}`)
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  getPage({ commit }, id) {
    return new Promise((resolve, reject) => {
      this._vm.$http
        .get(`content/getPage?id=${id}`)
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  addPage({ commit }, page) {
    return new Promise((resolve, reject) => {
      this._vm.$http
        .post('content/addPage', page)
        .then((response) => {
          resolve();
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  // eslint-disable-next-line no-empty-pattern
  addPages({}, pages) {
    return new Promise((resolve, reject) => {
      this._vm.$http
        .post('content/addPages', pages)
        .then(() => {
          resolve();
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  // Rebuilds a live page from the current blueprint as a new unpublished draft on the same slug. Resolves
  // with { pageId, created, alreadyInFlight } so the caller can tell a fresh draft from one already running.
  // eslint-disable-next-line no-empty-pattern
  rewritePage({}, pageId) {
    return new Promise((resolve, reject) => {
      this._vm.$http
        .post(`content/rewritePage?pageId=${pageId}`)
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  // Queues rewrites for the pages ticked in the list. Ids go in the body rather than the query string —
  // a selection can be dozens of them. Resolves with { queued, skipped }.
  // eslint-disable-next-line no-empty-pattern
  rewritePages({}, { pageIds, autoPublish }) {
    return new Promise((resolve, reject) => {
      this._vm.$http
        .post('content/rewritePages', { pageIds: pageIds, autoPublish: !!autoPublish })
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  // eslint-disable-next-line no-empty-pattern
  publishPage({}, pageId) {
    return new Promise((resolve, reject) => {
      this._vm.$http
        .post(`content/publishPage?pageId=${pageId}`)
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  // eslint-disable-next-line no-empty-pattern
  savePageJson({ commit }, page) {
    return new Promise((resolve, reject) => {
      this._vm.$http
        .post('content/savePageJson', page)
        .then((response) => {
          commit('SET_PAGE', response.data);
          resolve();
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  savePageTypeJson({ commit }, page) {
    return new Promise((resolve, reject) => {
      this._vm.$http
        .post('content/savePageTypeJson', page)
        .then((response) => {
          commit('lookup/SET_PAGE_TYPE', response.data, { root: true });
          resolve();
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  generatePageImages({ commit }, id) {
    return new Promise((resolve, reject) => {
      this._vm.$http
        .post(`content/generateImages?pageId=${id}`)
        .then((response) => {
          commit('SET_PAGE', response.data);
          resolve();
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  deletePage({ commit }, id) {
    return new Promise((resolve, reject) => {
      this._vm.$http
        .delete(`content/deletePage?pageId=${id}`)
        .then((response) => {
          commit('DELETE_PAGE', id);
          resolve();
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  // What people asked the Ask Mailing HQ box on /direct-mail, grouped by question and ordered
  // worst-answered first by the server.
  // eslint-disable-next-line no-empty-pattern
  getMailingHqQuestions({}, { days = 30, top = 200 } = {}) {
    return new Promise((resolve, reject) => {
      this._vm.$http
        .get('content/getMailingHqQuestions', { params: { days, top } })
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  // eslint-disable-next-line no-empty-pattern
  getNeedsGenerationPages({}) {
    return new Promise((resolve, reject) => {
      this._vm.$http
        .get('content/getNeedsGenerationPages')
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  // eslint-disable-next-line no-empty-pattern
  getNeedsGenerationSettings({}) {
    return new Promise((resolve, reject) => {
      this._vm.$http
        .get('content/getNeedsGenerationSettings')
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  // eslint-disable-next-line no-empty-pattern
  saveNeedsGenerationSettings({}, settings) {
    return new Promise((resolve, reject) => {
      this._vm.$http
        .post('content/saveNeedsGenerationSettings', settings)
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
};
